import { Router, NavigationError } from '@angular/router';
import { captureException } from '@portal-front/shared-util-sentry';

/**
 * Wzorce komunikatów błędów wskazujące, że Angular nie był w stanie pobrać
 * lazy-loadowanego chunka
 */
const CHUNK_LOAD_ERROR_PATTERNS: RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/i,
];

const RELOAD_FLAG_KEY = 'chunk-load-error-reload-at';
const RELOAD_COOLDOWN_MS = 30_000;

const isChunkLoadError = (error: unknown): boolean => {
  const message =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? error.message
        : (error as { message?: unknown })?.message;

  if (typeof message !== 'string') {
    return false;
  }

  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

const shouldReload = (): boolean => {
  try {
    const raw = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (!raw) return true;
    const last = Number(raw);
    if (!Number.isFinite(last)) return true;
    return Date.now() - last > RELOAD_COOLDOWN_MS;
  } catch {
    // sessionStorage może być zablokowany (Safari ITP, tryb prywatny) – wtedy lepiej spróbować niż zostawić białą stronę.
    return true;
  }
};

const markReloaded = (): void => {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch {
    /* noop */
  }
};

/**
 * Subskrybuje błędy nawigacji routera i przy stale-chunk errorze wymusza hard reload,
 * aby pobrać świeży index.html z aktualnymi hashami chunków.
 */
export const registerChunkLoadErrorRecovery = (router: Router): void => {
  router.events.subscribe((event) => {
    if (!(event instanceof NavigationError)) return;
    if (!isChunkLoadError(event.error)) return;

    // Raportuj do Sentry tylko gdy nie możemy się odzyskać (pętla / drugi raz).
    if (shouldReload()) {
      markReloaded();
      window.location.reload();
      return;
    }

    captureException(
      new Error(
        `[chunk-load] Reload nie pomógł – możliwy faktyczny brak chunka na serwerze. Original: ${
          (event.error as Error)?.message ?? String(event.error)
        }`,
      ),
    );
  });
};
